import {
  traceAudioAvailableIntentsQuery,
  useTraceAudioMutation,
} from "@common/api"
import type { AvailableIntent } from "@common/client"
import { useTraceUploadContext } from "@common/components/trace/trace-upload-context"
import { Button, type ButtonProps } from "@common/components/ui/button"
import { LoadingSpinner } from "@common/components/ui/loading-spinner"
import { LinearAudioLevels } from "@common/features/recording/audio-levels"
import type { RecordingState } from "@common/features/recording/hooks/use-whisper-transcription"
import { useTrace } from "@common/hooks/trace-provider"
import { useMicrophonePermission } from "@common/hooks/use-microphone-permission"
import { cn } from "@common/lib/utils"
import { useQuery } from "@tanstack/react-query"
import { Check, Mic, MicOff, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Permission } from "./permission"

type RecorderButtonProps = {
  icon: React.ComponentType<{ className?: string }>
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "type">

export function StartStop({
  icon: Icon,
  className,
  ...props
}: RecorderButtonProps): React.ReactElement {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      className={cn("text-muted-foreground", className)}
      {...props}
    >
      <Icon />
    </Button>
  )
}

function SecondaryIntent({
  intent,
  className,
  ...props
}: { intent: AvailableIntent } & ButtonProps): React.ReactElement {
  return (
    <Button
      variant="ghost"
      size="xs"
      className={cn(
        "px-1.5 text-extra-small-regular uppercase text-muted-foreground",
        className,
      )}
      type="button"
      {...props}
    >
      {intent.name}
    </Button>
  )
}

export function Recorder({
  recorder,
  selectedMode,
  disabled = false,
  onSubmit,
  onSent,
}: {
  recorder: RecordingState
  selectedMode?: string | null
  disabled?: boolean
  onSubmit?: () => void
  onSent?: (traceId: string) => void
}): React.ReactElement {
  const { mutateAsync } = useTraceAudioMutation()
  const [submitting, setSubmitting] = useState(false)
  const hasSentRef = useRef(false)
  const upload = useTraceUploadContext()
  const { ensureTrace, ensureConnection, getFeatures } = useTrace()
  const intentRef = useRef<string>(undefined)
  const { state: micPermission, request: requestMicPermission } =
    useMicrophonePermission()
  const [showPrompt, setShowPrompt] = useState(false)
  const [showDenied, setShowDenied] = useState(false)
  const deniedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { data: intents } = useQuery(traceAudioAvailableIntentsQuery())

  useEffect(() => {
    return () => clearTimeout(deniedTimerRef.current)
  }, [])

  const showPermissionDenied = useCallback(() => {
    setShowDenied(true)
    clearTimeout(deniedTimerRef.current)
    deniedTimerRef.current = setTimeout(() => setShowDenied(false), 1000)
  }, [])

  const startRecording = useCallback(async () => {
    if (recorder.status !== "idle" && recorder.status !== "complete") {
      return
    }

    try {
      hasSentRef.current = false
      await recorder.start()
    } catch {
      showPermissionDenied()
      return
    }

    void ensureTrace("attachment").catch(() => {})
  }, [recorder, showPermissionDenied, ensureTrace])

  const handleStartRecording = useCallback(
    async (intentId?: string) => {
      if (disabled || upload?.isUploading) {
        return
      }

      intentRef.current = intentId

      if (micPermission === "denied") {
        showPermissionDenied()
        return
      }

      if (micPermission === "prompt") {
        setShowPrompt(true)
        return
      }

      await startRecording()
    },
    [
      disabled,
      upload?.isUploading,
      micPermission,
      showPermissionDenied,
      startRecording,
    ],
  )

  useEffect(() => {
    if (recorder.status !== "complete" || submitting || hasSentRef.current) {
      return
    }

    hasSentRef.current = true
    setSubmitting(true)

    ensureConnection()
      .then(() => ensureTrace("attachment"))
      .then(async (traceId) => {
        const features = await getFeatures()
        const attachmentIds = (upload?.pending ?? []).flatMap((item) =>
          item.status === "done" && item.attachment ? [item.attachment.id] : [],
        )
        await mutateAsync({
          trace_id: traceId,
          intent_id: intentRef.current,
          mode_id: intentRef.current ? undefined : selectedMode,
          features,
          attachment_ids: attachmentIds,
          bodyTraceAudio: { file: recorder.audioFile },
        })
        upload?.clear()
        onSent?.(traceId)
      })
      .finally(() => {
        setSubmitting(false)
        hasSentRef.current = false
        recorder.reset()
        onSubmit?.()
      })
  }, [
    recorder,
    submitting,
    mutateAsync,
    ensureTrace,
    ensureConnection,
    getFeatures,
    onSent,
    onSubmit,
    selectedMode,
    upload,
  ])

  if (recorder.status === "recording" || recorder.status === "paused") {
    return (
      <div className="flex items-center justify-center gap-1 flex-1">
        <LinearAudioLevels
          levels={recorder.levels}
          className="m-auto"
          barCount={48}
        />
        <StartStop icon={X} onClick={() => recorder.stop()} />
        <StartStop icon={Check} onClick={() => recorder.accept()} />
      </div>
    )
  }

  if (recorder.status === "complete") {
    return <StartStop icon={LoadingSpinner} disabled />
  }

  if (showDenied) {
    return (
      <div className="flex items-center gap-1.5 text-greyscale-dark-grey">
        <MicOff className="size-4 shrink-0" />
        <span className="text-extra-small-medium">Permission denied</span>
      </div>
    )
  }

  if (showPrompt && micPermission === "prompt") {
    return (
      <Permission
        onMount={async () => {
          const granted = await requestMicPermission()
          setShowPrompt(false)
          if (!granted) {
            showPermissionDenied()
            return
          }
          await startRecording()
        }}
      />
    )
  }

  return (
    <>
      {!!intents &&
        intents.map((intent) => (
          <SecondaryIntent
            intent={intent}
            key={intent.id}
            disabled={disabled || !!upload?.isUploading}
            onClick={() => void handleStartRecording(intent.id)}
          />
        ))}
      <StartStop
        icon={Mic}
        aria-label="Dictate message"
        disabled={disabled || !!upload?.isUploading}
        onClick={() => void handleStartRecording()}
      />
    </>
  )
}
